import * as Ably from "ably";
import type {
  RealtimeChannel,
  ConnectionEvent,
  TokenRequest,
  ChannelEvent,
} from "ably";
import { BASE_URL } from "../constants";
import { tokenStorage } from "../utils/tokenStorage";
import {
  ERROR_MESSAGES,
  ABLY_CHAT_SETTINGS,
  CONNECTION_EVENTS,
  CHAT_EVENTS,
} from "../utils/constants";

let realtime: Ably.Realtime | null = null;
let boundUserId: string | null = null;
let globalPresenceChannel: Ably.RealtimeChannel | null = null;
let activeRoomChannel: Ably.RealtimeChannel | null = null;
type AuthQueryKey = "clientId" | "userId";
let resolvedAuthQueryKey: AuthQueryKey | null = null;
const QUERY_KEY = {
  CLIENT_ID: "clientId",
  USER_ID: "userId",
} as const;

function enterGlobalPresenceSync(client: Ably.Realtime) {
  if (!client.auth.clientId) {
    return;
  }
  const channel = client.channels.get(ABLY_CHAT_SETTINGS.GLOBAL_PRESENCE_CHANNEL);
  globalPresenceChannel = channel;
  void channel.presence.enter(CHAT_EVENTS.ONLINE).catch(() => {
    /* non-fatal */
  });
}

function attachGlobalPresenceOnConnect(client: Ably.Realtime) {
  const run = () => enterGlobalPresenceSync(client);
  if (client.connection.state === CONNECTION_EVENTS.CONNECTED) {
    run();
    return;
  }
  client.connection.once(CONNECTION_EVENTS.CONNECTED as ConnectionEvent, run);
}

/**
 * Ably Realtime singleton for chat. Uses GET /chat/auth with Bearer token;
 * unwraps envelope and passes TokenRequest in `data` to the SDK.
 *
 * Do not set `clientId` on `Realtime` options: the TokenRequest from the
 * server includes `clientId`. If it differs from a constructor `clientId`,
 * Ably reports "invalid clientId for credentials" on attach/presence.
 */
export function initAblyForUser(userId: string): Ably.Realtime {
  if (realtime && boundUserId === userId) {
    return realtime;
  }
  resetAbly();

  boundUserId = userId;
  const client = new Ably.Realtime({
    authCallback: async (_tokenParams, callback) => {
      try {
        const token = tokenStorage.getTokens().userAccessToken;
        if (!token) {
          callback(ERROR_MESSAGES.CHAT_AUTH_NO_ACCESS_TOKEN, null);
          return;
        }
        const tryAuthRequest = async (queryKey: AuthQueryKey) => {
          const url = `${BASE_URL}/chat/auth?${queryKey}=${encodeURIComponent(userId)}`;
          const res = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const json = await res.json();
          return { res, json };
        };

        const finish = (data: unknown, keyUsed: AuthQueryKey): void => {
          if (!data || typeof data !== "object" || !("keyName" in data)) {
            callback(ERROR_MESSAGES.CHAT_AUTH_INVALID_RESPONSE, null);
            return;
          }
          resolvedAuthQueryKey = keyUsed;
          callback(null, data as TokenRequest);
        };

        if (resolvedAuthQueryKey) {
          const { res, json } = await tryAuthRequest(resolvedAuthQueryKey);
          if (!res.ok) {
            const msg =
              (typeof json?.message === "string" && json.message) ||
              `Chat auth failed: ${res.status}`;
            callback(msg, null);
            return;
          }
          finish(json?.data, resolvedAuthQueryKey);
          return;
        }

        let attempt = await tryAuthRequest(QUERY_KEY.CLIENT_ID);
        let keyUsed: AuthQueryKey = QUERY_KEY.CLIENT_ID;
        if (!attempt.res.ok) {
          attempt = await tryAuthRequest(QUERY_KEY.USER_ID);
          keyUsed = QUERY_KEY.USER_ID;
        }
        if (!attempt.res.ok) {
          const msg =
            (typeof attempt.json?.message === "string" &&
              attempt.json.message) ||
            `Chat auth failed: ${attempt.res.status}`;
          callback(msg, null);
          return;
        }
        finish(attempt.json?.data, keyUsed);
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : String(err ?? ERROR_MESSAGES.CHAT_AUTH_FAILED);
        callback(msg, null);
      }
    },
  });

  realtime = client;
  attachGlobalPresenceOnConnect(client);
  return client;
}

export function getAblyRealtime(): Ably.Realtime | null {
  return realtime;
}

/** Exported for room `subscribe()` promise rejection when teardown races attach (e.g. Strict Mode). */
export function isBenignChannelTransitionError(err: unknown): boolean {
  const msg =
    err && typeof err === "object" && "message" in err
      ? String((err as { message: unknown }).message)
      : String(err ?? "");
  return (
    msg.includes(ABLY_CHAT_SETTINGS.SUPERSEDED) ||
    msg.includes(ABLY_CHAT_SETTINGS.ATTACH_REQUEST_SUPERSEDED) ||
    msg.includes(ABLY_CHAT_SETTINGS.DETACH_REQUEST_SUPERSEDED) ||
    msg.includes(ABLY_CHAT_SETTINGS.CHANNEL_DETACHED)
  );
}

/**
 * Room channels only subscribe to messages — we never `presence.enter` on them.
 * Calling `presence.leave` here caused Ably to throw (no presence member / clientId edge cases).
 *
 * If we `detach()` while still `attaching` (subscribe triggers attach), Ably emits
 * "Attach request superseded by a subsequent detach request". Wait for `attached`
 * (or `failed`) before detaching; otherwise defer with `queueMicrotask`.
 */
function releaseRoomChannel(channel: RealtimeChannel | null): void {
  if (!channel) {
    return;
  }

  const runDetach = () => {
    void channel.detach().catch((err: unknown) => {
      if (!isBenignChannelTransitionError(err)) {
        /* non-fatal race or connection teardown */
      }
    });
  };

  const scheduleDetach = () => {
    queueMicrotask(runDetach);
  };

  switch (channel.state) {
  case ABLY_CHAT_SETTINGS.CHANNEL_STATE.INITIALIZED:
  case ABLY_CHAT_SETTINGS.CHANNEL_STATE.DETACHED:
  case ABLY_CHAT_SETTINGS.CHANNEL_STATE.FAILED:
    return;
  case ABLY_CHAT_SETTINGS.CHANNEL_STATE.ATTACHING:
    channel.once(ABLY_CHAT_SETTINGS.CHANNEL_STATE.ATTACHED as ChannelEvent, () => {
      scheduleDetach();
    });
    channel.once(ABLY_CHAT_SETTINGS.CHANNEL_STATE.FAILED as ChannelEvent, () => {
      /* Attach aborted; nothing to detach. */
    });
    return;
  case ABLY_CHAT_SETTINGS.CHANNEL_STATE.DETACHING:
    return;
  default:
    try {
      scheduleDetach();
    } catch {
      /* detach() can throw synchronously in edge states */
    }
  }
}

/** Track the room channel so logout can detach cleanly. */
export function setActiveRoomChannel(
  channel: Ably.RealtimeChannel | null
): void {
  if (activeRoomChannel && activeRoomChannel !== channel) {
    releaseRoomChannel(activeRoomChannel);
  }
  activeRoomChannel = channel;
}

function safeGlobalPresenceLeave(channel: Ably.RealtimeChannel | null): void {
  if (!channel || !realtime) {
    return;
  }
  const clientId = realtime.auth.clientId;
  if (!clientId) {
    return;
  }
  void channel.presence.leave().catch(() => {
    /* leave() is async; errors are non-fatal on teardown */
  });
}

export function leaveGlobalPresence(): void {
  if (globalPresenceChannel) {
    safeGlobalPresenceLeave(globalPresenceChannel);
    globalPresenceChannel = null;
  }
}

/** Mobile `chatRealtimeService.enterGlobalPresence` — await after `connect`. */
export async function enterGlobalPresence(): Promise<void> {
  const client = getAblyRealtime();
  if (!client?.auth.clientId) {
    return;
  }
  const channel = client.channels.get(ABLY_CHAT_SETTINGS.GLOBAL_PRESENCE_CHANNEL);
  globalPresenceChannel = channel;
  await channel.attach();
  await channel.presence.enter(CHAT_EVENTS.ONLINE);
}

export function resetAbly(): void {
  if (activeRoomChannel) {
    releaseRoomChannel(activeRoomChannel);
    activeRoomChannel = null;
  }
  if (globalPresenceChannel) {
    safeGlobalPresenceLeave(globalPresenceChannel);
    globalPresenceChannel = null;
  }
  if (realtime) {
    try {
      realtime.close();
    } catch {
      /* noop */
    }
    realtime = null;
  }
  boundUserId = null;
  resolvedAuthQueryKey = null;
}
